import type { AnnotationSize } from '@/services/hooks/template_attributes'
import {
  DEFAULT_KONVA_STROKE,
  DEFAULT_KONVA_STROKE_WIDTH,
  DEFAULT_KONVA_TEXT_FONT_SIZE,
  DEFAULT_KONVA_TEXT_PADDING,
  LEGACY_ANNOTATION_BOUNDING_BOX_INSET,
  getAnnotationSizeNumber,
  getLegacyStrokeWidthForType,
  makeBoundsGeometry,
  makePointsGeometry,
  KONVA_ANNOTATION_SCHEMA_VERSION,
  type AnnotationToolPreset,
  type KonvaAnnotationGeometry,
  type KonvaAnnotationTemplate,
} from './annotationSchema'
import { HARDWARE_ICON_SOURCES } from './annotationIconSources'
import {
  getAnnotationKindForLegacyType,
  isFreehandAnnotationType,
  isLegacyLineBasedAnnotationType,
} from './annotationMetadata'

export const LEGACY_TRIANGLE_Y_OFFSET = 3
export const LEGACY_ARROW_TEXT_WIDTH = 100
export const LEGACY_ARROW_TEXT_HEIGHT = 20
export const LEGACY_ARROW_TEXT_CORNER_RADIUS = 6
export const LEGACY_ARROW_TEXT_MAX_LENGTH = 10

const DEFAULT_LINE_LENGTH = 96
const FREEHAND_PREVIEW_SIZE = 32
const FREEHAND_STROKE_WIDTHS: Record<string, number> = {
  'freehand--thin': 1,
  'freehand--medium': 5,
  'freehand--thick': 10,
}

export const getFreehandStrokeWidth = (type?: string) =>
  type ? (FREEHAND_STROKE_WIDTHS[type] ?? DEFAULT_KONVA_STROKE_WIDTH) : 5

export const getLegacyStroke = (strokeColor?: string) =>
  strokeColor ?? DEFAULT_KONVA_STROKE

export const getArrowTextPlacement = (
  legacyType: string,
): 'inside' | 'middle' | 'bottom' => {
  if (legacyType.includes('--middle')) return 'middle'
  if (legacyType.includes('--bottom')) return 'bottom'
  return 'inside'
}

export const getLegacyBounds = (annotation: {
  size?: AnnotationSize
  type?: string
}): { width: number; height: number; inset: number } => {
  const numericSize = getAnnotationSizeNumber(annotation.size)
  const boundingBoxWidth =
    annotation.size && typeof annotation.size === 'number'
      ? annotation.size * 4
      : numericSize
  const boundingBoxHeight =
    annotation.size && typeof annotation.size === 'number'
      ? annotation.size * 4
      : numericSize
  const inset = Math.min(
    LEGACY_ANNOTATION_BOUNDING_BOX_INSET,
    Math.max(Math.floor(boundingBoxWidth / 8), 2),
    Math.max(Math.floor(boundingBoxHeight / 8), 2),
  )
  const width = Math.max(boundingBoxWidth - inset * 2, 8)
  const height = Math.max(boundingBoxHeight - inset * 2, 8)

  if (
    annotation.type !== 'circle' &&
    annotation.type !== 'square' &&
    annotation.type !== 'triangle'
  ) {
    return { width: boundingBoxWidth, height: boundingBoxHeight, inset: 0 }
  }

  if (annotation.type === 'triangle') {
    return {
      width,
      height: Math.round((width * Math.sqrt(3)) / 2),
      inset,
    }
  }

  if (annotation.type === 'circle') {
    return { width, height: width, inset }
  }

  if (annotation.type === 'square') {
    return { width, height: width, inset }
  }

  return { width, height, inset }
}

export const getAnnotationToolPreset = ({
  bubbleValue,
  size,
  strokeColor,
  type,
}: {
  bubbleValue?: string
  size?: AnnotationSize
  strokeColor?: string
  type?: string
}): AnnotationToolPreset => {
  const stroke = getLegacyStroke(strokeColor)
  const strokeWidth = getLegacyStrokeWidthForType({ size, type })
  const baseStyle = {
    stroke,
    strokeWidth,
    fill: null,
    opacity: 1,
  }

  if (isFreehandAnnotationType(type)) {
    return {
      kind: 'freehand',
      defaultGeometry: makePointsGeometry({
        x: 0,
        y: 0,
        points: [
          0,
          FREEHAND_PREVIEW_SIZE,
          FREEHAND_PREVIEW_SIZE * 0.35,
          FREEHAND_PREVIEW_SIZE * 0.2,
          FREEHAND_PREVIEW_SIZE * 0.7,
          FREEHAND_PREVIEW_SIZE * 0.8,
          FREEHAND_PREVIEW_SIZE,
          0,
        ],
        width: FREEHAND_PREVIEW_SIZE,
        height: FREEHAND_PREVIEW_SIZE,
      }),
      defaultStyle: {
        ...baseStyle,
        strokeWidth: getFreehandStrokeWidth(type),
      },
      legacyType: type,
    }
  }

  if (isLegacyLineBasedAnnotationType(type)) {
    const kind = getAnnotationKindForLegacyType(type)

    return {
      kind,
      defaultGeometry: makePointsGeometry({
        x: 0,
        y: 0,
        points: [0, 0, DEFAULT_LINE_LENGTH, 0],
        width: DEFAULT_LINE_LENGTH,
        height: 0,
      }),
      defaultStyle: baseStyle,
      defaultText: type.includes('arrow-with-text')
        ? {
            enabled: true,
            value: '',
            align: 'center',
            verticalAlign: 'middle',
            placement: getArrowTextPlacement(type),
            padding: DEFAULT_KONVA_TEXT_PADDING,
            fontSize: DEFAULT_KONVA_TEXT_FONT_SIZE,
            color: stroke,
            boxWidth: LEGACY_ARROW_TEXT_WIDTH,
            boxHeight: LEGACY_ARROW_TEXT_HEIGHT,
            boxCornerRadius: LEGACY_ARROW_TEXT_CORNER_RADIUS,
            maxLength: LEGACY_ARROW_TEXT_MAX_LENGTH,
          }
        : undefined,
      legacyType: type,
      bubbleValue,
    }
  }

  const kind = getAnnotationKindForLegacyType(type)
  const { width, height } = getLegacyBounds({ size, type })

  return {
    kind,
    defaultGeometry: makeBoundsGeometry({
      x: 0,
      y: 0,
      width,
      height,
      cornerRadius: type === 'roundRect' ? 8 : undefined,
    }),
    defaultStyle: baseStyle,
    legacyType: type,
    iconName: kind === 'icon' ? type : undefined,
    iconSrc: kind === 'icon' ? HARDWARE_ICON_SOURCES[type ?? ''] : undefined,
  }
}

export const createKonvaAnnotationFromPreset = ({
  preset,
  x,
  y,
  z,
  subPageId,
}: {
  preset: AnnotationToolPreset
  x: number
  y: number
  z?: number
  subPageId?: string
}): KonvaAnnotationTemplate => {
  const geometry: KonvaAnnotationGeometry =
    preset.defaultGeometry.mode === 'bounds'
      ? {
          ...preset.defaultGeometry,
          mode: 'bounds',
          x,
          y,
          z: z ?? preset.defaultGeometry.z,
        }
      : {
          ...preset.defaultGeometry,
          mode: 'points',
          x,
          y,
          z: z ?? preset.defaultGeometry.z,
        }

  return {
    schema: KONVA_ANNOTATION_SCHEMA_VERSION,
    kind: preset.kind,
    geometry,
    style: {
      stroke: DEFAULT_KONVA_STROKE,
      strokeWidth: DEFAULT_KONVA_STROKE_WIDTH,
      fill: null,
      ...preset.defaultStyle,
    },
    text: preset.defaultText,
    subPageId,
    legacyType: preset.legacyType,
    bubbleValue: preset.bubbleValue,
    iconName: preset.iconName,
    iconSrc: preset.iconSrc,
  }
}
